import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property} from 'lit/decorators.js';
import { voyageRadioButtonsStyle, voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import '../../voyage-popup/src/voyage-popup';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import {  ELEMENT_DATA, keysToConvert, mapWeatherLimits, WEATHER_LIMIT_DATA, weatherlimit, WeatherLimitElement } from '../../../src/models/vessel-weather-model';
import {  ConfigService, getWeatherLimits } from '../../../src/services/config-service';
import { VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { VoyageImoEventNames } from '../../../src/models/voyage-imo-model-impl';
import { HydraRoutingService } from '../../../src/services/hydra-routing-service';

export class VoyageWeatherLimits extends LitElement
{
    @property({type: Boolean}) shown: boolean = false;
    @property({ attribute: false }) private _unit: string = "m/s";
    @property({ type: Array }) _isValidInput: string[] = [];
    private _tempInputValue: string | null = null;

    // Initialize separate variables for avoid and warn limits
    private _weatherAvoidLimits: weatherlimit = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));
    private _weatherWarnLimits: weatherlimit = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));

    private async _fetchWeatherLimits() {
        try {
            const data = await ConfigService.fetchConfigData();

            // Normalize: ensure all fields from WEATHER_LIMIT_DATA are present
            const normalizeLimits = (source: any, template: any) => {
                const result: any = {};
                for (const key in template) {
                    if (typeof template[key] === 'object' && template[key] !== null && !Array.isArray(template[key])) {
                        result[key] = normalizeLimits(source?.[key] || {}, template[key]);
                    } else {
                        result[key] = source?.[key] === undefined ? template[key] : source[key];
                    }
                }
                return result;
            };

            this._weatherAvoidLimits = normalizeLimits(data?.avoid, WEATHER_LIMIT_DATA);
            this._weatherWarnLimits = normalizeLimits(data?.warn, WEATHER_LIMIT_DATA);

            this._getWeatherLimits = {
                avoid: this._weatherAvoidLimits,
                warn: this._weatherWarnLimits
            };

            // Request update and wait for rendering to complete
            await this.requestUpdate();
            await this.updateComplete;

            // Setting weather limits in local storage on initial render
            const mappedRouteLimits = mapWeatherLimits(this._weatherAvoidLimits, this._weatherWarnLimits);
            VoyageRoutingModel!.setWeatherLimits(mappedRouteLimits);

            // dispatching max roll angle limit for polar chart capture
            if (data?.avoid?.maxRollAngle !== undefined && data?.avoid?.maxRollAngle !== null) {
                this.dispatchEvent(
                    new CustomEvent('max-roll-angle-limit', {
                        detail: { maxRollAngle: data.avoid.maxRollAngle },
                        bubbles: true,
                        composed: true,
                    })
                );
            }
        } catch (error) {
            console.error('Failed to fetch weather limits:', error);
            this._weatherAvoidLimits = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));
            this._weatherWarnLimits = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));

            this._getWeatherLimits = {
                avoid: this._weatherAvoidLimits,
                warn: this._weatherWarnLimits
            };

            // Request update and wait for rendering to complete
            await this.requestUpdate();
            await this.updateComplete;
        }

    }
    
    constructor()
    {
        super();
        
        this._elements = ELEMENT_DATA;
        this._weatherAvoidLimits = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));
        this._weatherWarnLimits = JSON.parse(JSON.stringify(WEATHER_LIMIT_DATA));
        this._fetchWeatherLimits();
        this._onClose = this._onClose.bind(this);
        this._onAccept = this._onAccept.bind(this);
        VoyageImoModel!.addEventListener(VoyageImoEventNames.ImoUpdated, () => {
            this._fetchWeatherLimits();
            this._fetchIfKrugerModelIsAvailable();
        })
        this._fetchIfKrugerModelIsAvailable = this._fetchIfKrugerModelIsAvailable.bind(this);
        this._fetchIfKrugerModelIsAvailable();

    }

    private async _fetchIfKrugerModelIsAvailable() {
        const krugerModelAvailable = await HydraRoutingService.checkIfKrugerModelIsAvailable();
        localStorage.setItem('krugerModelAvailable', JSON.stringify(krugerModelAvailable));
    }

    
    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                font-family: MaerskText;
            }

            #container {
                display: grid;
                margin: 10px 20px 20px 20px;
                align-items: center;
                grid-column-gap: 15px;
            }

            .cell, input[type="radio"].cell {
                margin-top: 15px;
            }

            .cell[connected] {
                margin-top: 5px;
            }

            .form-header {
                margin-top: 0;
                text-align: center;
                color: var(--neutrals-grey-02, black);
                font-size: 18px;
                font-weight: bold;
            }

            .name {
                text-align: right;
                color: var(--neutrals-grey-02, black);
                font-size: 14px;
            }

            .name[connected] {
                position: relative;
                color: var(--neutrals-grey-04, grey);
            }

            .name[connected]:before {
                content: "";
                box-sizing: border-box;
                position: absolute;
                bottom: 50%;
                transform: translateX(-15px);
                width: 10px;
                height: 36px;
                border-left: 1px solid var(--neutrals-grey-05, grey);
                border-bottom: 1px solid var(--neutrals-grey-05, grey);
            }

            .numberInput {
                text-align: right;
                height: 41px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
                position: relative;
            }

            .valid:invalid {
                background: #ffcccb;
                width: 130px;
                border-radius: 5px;
            }

            .valid:focus-within {
                outline: none;
            }

            .numberInput[disabled] {
                background-color: rgba(217, 217, 217, 0.23);
            }

            .numberInput:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                outline: none;
            }

            .inlineInput {
                text-align: right;
                width: 35px;
                border: none;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
            }

            .periodInput {
                color: var(--neutrals-grey-04, grey);
                padding-left: 10px;
                padding-right: 10px;
                display: flex;
                align-items: center;
            }

            .inlineInput:invalid {
                background: #ffcccb;
                width: 40px;
                border-radius: 5px;
            }

            .cell[disabled] {
                background-color: rgba(217, 217, 217, 0.23);
            }

            .dropdown {
                box-sizing: border-box;
                height: 41px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
                text-indent: 5px;
            }

            .dropdown[disabled] {
                background-color: rgba(217, 217, 217, 0.23);
            }

            .text{
                font-size: 12px;
                margin-left: 2px;
            }

            .header {
                display: flex;
                align-items: center;
                gap: 7.5rem;
                font-family: var(--component-paragraph-ff, serif);
                color: var(--neutrals-grey-02, black);
                font-size: 16px;
                line-height: 24px;
                padding-top: 12px;
                padding-bottom: 12px;
                padding-left: 12px;
                margin: 0;
                background: var(--neutrals-grey-09);
                border-bottom: 1px solid var(--neutrals-grey-07);
            }

             #closeButton {
                position: absolute;
                top: 26px;
                right: 10px;
                padding: 0;
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
                fill: var(--neutrals-grey-04, grey);
            }

            #closeButton > svg {
                display: block;
                width: 10px;
                margin-right: 5px;
            }
            
            .radio-button-display {
                display: flex;
                align-items: center;
            }
            
            .headerText {    
                font-size: 16px;
            }
            
            .padding {
                padding-right: 10px;
            }
            
            .questionCircle {
                color: red;
                transform: translateY(25%);
                padding-left: 25%;
            }
            .valid {
                width: 130px;
                height: 28px;
                margin-top: 5px;
                margin-right: 4px;
                border: none;
                text-align: right;
                font-size: 14px;
                font-family: MaerskText;
                padding-right: 8px;
                background: inherit;
            }
            .input-container{
                position: relative;
            }
            .number-validation-icon {
                position: absolute; 
                right: 10px; 
                top: 50%; 
                transform: translateY(-50%); 
                display: flex;
                align-items: center;
            }
            .number-validation-icon-period {
                position: absolute; 
                right: 5px; 
                top: 35%; 
                transform: translateY(-50%); 
                display: flex;
                align-items: center;
            }
            voyage-tooltip-proxy {
                position: absolute;
                top: 100%; 
                left: 90%;
                transform: translateX(-50%);
                white-space: nowrap;
            }
            .padding-bottom{
                padding-bottom: 15px;
            }
            #headercontainer{
                position: relative;
            }

             .header-row {
                display: grid;
                margin: 20px 20px 10px 20px;
                grid-template-columns: auto 150px 130px;
                align-items: center;
                grid-column-gap: 15px;
            }
            .row {
                display: grid;
                padding: 10px 20px;
                grid-template-columns: auto 150px 130px;
                align-items: center;
                grid-column-gap: 15px;
                background: #f1ecf8;
            }

            .row:nth-child(1) {
                background: #ffeedb;
                border-radius: 10px 10px 0 0;
            }
            
            .legend-container {
                padding: 0px 20px;
            }

            .legend {
                list-style: none;
                padding: 0;
                margin: 0;
                font-size: 12px;
                display: flex;
                gap: 16px;
            }

            .legend li {
                display: flex;
                align-items: center;
                margin-bottom: 6px;
            }

            .color-circle {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                display: inline-block;
                margin-right: 8px;
                border: 1px solid #ccc;
            }

            .pr {
                background: #ffeedb;
            }

            .hw {
                background-color: #f1ecf8;
            }
            
        `];
    }
    @property({ attribute: false }) private _getWeatherLimits: getWeatherLimits = {avoid: WEATHER_LIMIT_DATA, warn: WEATHER_LIMIT_DATA};

    

    private async _postWeatherLimits(userData: any) {
        try {
            const response = await ConfigService.postConfigData(userData);

            if (response) {
                console.log('Response data:', response);
                // Handle the response data as needed
            }
        } catch (error) {
            console.error('Failed to post weather limits:', error);
        }
    }

    render()
    {
        this._checkValidity();
        return html`
        <voyage-popup ?shown=${this.shown} accept-button-text="Apply"  no-header ?disable-accept-button=${!this._isValid}
                      @close=${this._onClose} @accept=${this._onAccept} >
        <div>
        <div id="headercontainer">
            <button id="closeButton" @click=${() => this._onClose() }>
                    ${SvgIcons.lightTimes}
                </button>
            <slot name="header">
                <div class="header">
                    <h1 class="headerText">Weather Limits</h1>
                    <div class="radio-button-display">
                        <div class="padding">Change wind unit:</div>
                            <div class="padding">
                            <mc-radio
                                id="m/s"
                                name="windUnit"
                                label="m/s"
                                @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                                ?checked=${this._unit == "m/s"}
                            />
                            </div>
                            <div class="padding">
                            <mc-radio
                                id="kt"
                                name="windUnit"
                                label="kt"
                                @change=${(evt:InputEvent) => this._handleUnitChange(evt)}
                                ?checked=${this._unit == "kt"}
                            />
                            </div>
                        <div>
                     </div>
                </div>
            </slot>
        </div>
                <div class="header-row">
                    <div></div>
                    <div class="form-header padding-bottom">Vessel threshold</div>
                    <div>
                    <div class="form-header">Maersk's policy</div>
                    <div class="text">(Read only)</div>
                    </div>  
                </div> 
                <div class="legend-container">
                    <ul class="legend">
                        <li><span class="color-circle pr"></span> Parametric roll</li>
                        <li><span class="color-circle hw"></span>Heavy weather</li>
                    </ul>
                </div>
                <div id="container">   
                    ${ this._elements.map( element => this._renderInputElement(element) )}
                </div>
    </div>
        </voyage-popup>
        `;
    }

    @property({attribute: false}) private _isValid: boolean = true;
    private _elements: WeatherLimitElement[];

    private _renderInputElement(element: WeatherLimitElement)
    {
        if (element.dropdown)
        {
            return this._renderDropdownInput(element);
        }
        else if (element.connectedTo)
        {
            return this._renderPeriodInput(element);
        }
        else
        {
            return this._renderNumberInput(element);
        }
    }

    private _handleInputBlur(evt: InputEvent, element: WeatherLimitElement, min: number, max: number | null) {
        const target = <HTMLInputElement>evt.target;
        const inputValue = target.value === "" ? null : parseFloat(target.value);
        let convertedValue = inputValue;
        
        if (this._unit === "kt" && keysToConvert.includes(element.name)) {
            convertedValue = inputValue !== null ? parseFloat((inputValue / 1.94384).toFixed(1)) : null;
        }
    
        (this._weatherWarnLimits[element.name] as number | null) = convertedValue !== null ? convertedValue : inputValue;
        this.requestUpdate();
        this._validateNumberInput(element, inputValue, min, max);
    }

    private _renderNumberInput(element: WeatherLimitElement)
    {
        const min = element.min || 0;
        const warn = this._weatherWarnLimits ? (<number|null>this._weatherWarnLimits[element.name]) : null;
        const avoid = this._weatherAvoidLimits ? (<number>this._weatherAvoidLimits[element.name]) : null;
        const warnValue = this._unit === "kt" && keysToConvert.includes(element.name) ?
            this._convertValue(warn, 1.94384) : warn;
        const avoidValue = this._unit === "kt" && keysToConvert.includes(element.name) ?
            this._convertValue(avoid, 1.94384) : avoid;

        
        const maxCondition = `${element.name}+max`;
        const showValidationIcon = this._isValidInput.includes(maxCondition);

        return html`
        <div class="row">
            <div class="cell name">${element.text}</div>
            <div class="cell numberInput">
                <div class="input-container">
                    <input data-test=${element.name+"-warn"} class="valid"
                        .value=${warnValue != null ? warnValue : ""}
                        type="number" step="0.1" min=${min} max=${avoidValue} 
                        @input=${(e: InputEvent) => {
                        this._tempInputValue = (e.target as HTMLInputElement).value;
                    }}
                    @blur=${(e: InputEvent) => this._handleInputBlur(e, element, min, avoidValue)}/> 
                    ${showValidationIcon ? html`
                    <div class="number-validation-icon">
                        <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                        <voyage-tooltip-proxy
                            text="Maximum value is ${avoidValue}"
                            html-content
                            .positionPriority=${["top"]}
                        >
                        </voyage-tooltip-proxy>
                    </div>
                    ` : html``}
                </div>
            </div>
            <input data-test=${element.name+"-avoid"} class="cell numberInput"
                .value="${avoidValue != null ? avoidValue : ""}" disabled
                type="number" step="0.1" min=${min} .value="0">
        </div>
    `;
    }

    private _renderPeriodInput(element: WeatherLimitElement)
    {
        const warnMin = this._weatherWarnLimits ? (<{max: number|null; min: number|null}>this._weatherWarnLimits[element.name])?.min : null;
        const warnMax = this._weatherWarnLimits ? (<{max: number|null; min: number|null}>this._weatherWarnLimits[element.name])?.max : null;
        const avoidMin = this._weatherAvoidLimits ? (<{max: number|null; min: number|null}>this._weatherAvoidLimits[element.name])?.min : null;
        const avoidMax = this._weatherAvoidLimits ? (<{max: number|null; min: number|null}>this._weatherAvoidLimits[element.name])?.max : null;

        const min = element.min || 0;
        const max = element.max;
        const onValueInput = (evt: InputEvent, type: "min"|"max") => {
            const target = <HTMLInputElement>evt.target;
            const value = target.value !== "" ?  Number(target.value) : null;
            (<{max: number|null; min: number|null}>this._weatherWarnLimits[element.name])[type] = value;

            this._validatePeriodInput(element, value, max, type);
            this.requestUpdate();
        };
        const maxCondition = `${element.name}+max`;
        const minCondition = `${element.name}+min`;

        return html`
            <div class="row">
            <div class="cell name" connected>${element.text}</div>
            <div class="cell numberInput periodInput" connected>
                <div>from</div>
                <div class="input-container">
                <input data-test=${element.name+"-warn-from"} class="inlineInput" type="number" step="1" min="0" 
                .value=${warnMin} min=${min} max=${warnMax}
                       @input=${(e: InputEvent) => onValueInput(e, "min")}>
                       ${this._isValidInput.includes(minCondition) ? html`
                <div class="number-validation-icon-period">
                    <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                    <voyage-tooltip-proxy
                        text="'from' value is more than 'to' value"
                        html-content
                        .positionPriority=${["top"]}
                    >
                    </voyage-tooltip-proxy>
                </div>
                ` : html``}
                </div>
                <div>to</div>
                <div class="input-container">
                <input data-test=${element.name+"-warn-to"} class="inlineInput" type="number" step="1" min="0"  max=${max} .value=${warnMax}
                       @input=${(e: InputEvent) => onValueInput(e, "max")}/>
                       ${this._isValidInput.includes(maxCondition) ? html`
                <div class="number-validation-icon-period">
                    <span class="questionCircle">${SvgIcons.customLightQuestionCircle}</span>
                    <voyage-tooltip-proxy
                        text="Maximum value is ${max}"
                        html-content
                        .positionPriority=${["top"]}
                    >
                    </voyage-tooltip-proxy>
                </div>
                ` : html``}
                </div>
            </div>
            <div class="cell numberInput periodInput" connected disabled >
                <div>from</div>
                <input data-test=${element.name+"-avoid-from"} class="inlineInput" type="number" step="1" min="0" .value=${avoidMin}
                disabled >
                <div>to</div>
                <input data-test=${element.name+"-avoid-to"} class="inlineInput" type="number" step="1" min="0" .value=${avoidMax}
                disabled >
            </div>
        </div>
        `;
    }

    private _renderDropdownInput(element: WeatherLimitElement)
    {
        const warnLimit = this._weatherWarnLimits ? this._weatherWarnLimits[element.name] : null;
        const avoidLimit = this._weatherAvoidLimits ? this._weatherAvoidLimits[element.name] : null;

        const onValueInput = (evt: InputEvent) => {
            const target = <HTMLSelectElement>evt.target;
            const value = target.value;
            (this._weatherWarnLimits[element.name] as number | null) = value === "" ? null : parseFloat(value);
            this.requestUpdate();
        };
        return html`
        <div class="row">
            <div class="cell name">${element.text}</div>
            <select data-test=${element.name+"-warn"} class="cell dropdown" .value=${warnLimit} @input=${(e: InputEvent) => onValueInput(e)}>
                <option value="">Select...</option>
                ${element.dropdown!.map( (value, index) => html`
                <option value=${index} ?selected=${warnLimit === index}>${value}</option>
                `)}
            </select>
            <select data-test=${element.name+"-avoid"} class="cell dropdown" .value=${avoidLimit} disabled>
                <option value="">Select...</option>
                ${element.dropdown!.map( (value, index) => html`
                <option value=${index} ?selected=${avoidLimit === index}>${value}</option>
                `)}
            </select>
        </div>
        `;
    }

    private _handleUnitChange(event: any) {
        const newUnit = event.target.id;
        if (this._unit === newUnit) return;
        this._unit = newUnit;
    
        if (newUnit === "kt") {
          this._convertToKt();
        } else if (newUnit === "m/s") {
          this._convertToMs();
        }
    
        this.requestUpdate();
      }


      private _convertToKt() {
        this._elements = this._elements.map((element) =>
          this._convertElementToKt(element)
        );
      }
    
      private _convertElementToKt(
        element: WeatherLimitElement
      ): WeatherLimitElement {
        if (element.unit === "m/s") {
          element.max = parseFloat((element.max! * 1.94384).toFixed(1));
          element.unit = "kt";
        }
        return element;
      }
    
      private _convertToMs() {
        this._elements = this._elements.map((element) =>
          this._convertElementToMs(element)
        );
      }
    
      private _convertElementToMs(
        element: WeatherLimitElement
      ): WeatherLimitElement {
        if (element.unit === "kt") {
          element.max = parseFloat((element.max! / 1.94384).toFixed(1));
          element.unit = "m/s";
        }
        return element;
      }
    
    private _convertValue(value: number | null, conversionFactor: number): number | null {
        if (value === null) return null;
        return Number((value * conversionFactor).toFixed(1));
    }

      
    private async _checkValidity()
    {
        // Wait for render
        await this.updateComplete;

        // Check overall validity
        const inputs = <NodeListOf<HTMLInputElement>>this.shadowRoot!.querySelectorAll(`input[type="number"]`);
        let isValid = true;
        for (const input of inputs)
        {
            if (!input.validity.valid) {
                isValid = false;
                break;
            }
        }

        this._isValid = isValid;
    }

    private _validateNumberInput(
        element: WeatherLimitElement,
        value: number | null,
        min: number,
        max: number | null
      ) {
        const minCondition = `${element.name}+min`;
        const maxCondition = `${element.name}+max`;
    
    
        if (value !== null) {
          if (value < min) {
            if (!this._isValidInput.includes(minCondition)) {
              this._isValidInput = [...this._isValidInput, minCondition];
            }
          } else {
            this._isValidInput = this._isValidInput.filter(
              (item) => item !== minCondition
            );
          }
    
          if (value > (max ?? Infinity)) {
            if (!this._isValidInput.includes(maxCondition)) {
              this._isValidInput = [...this._isValidInput, maxCondition];
            }
          } else {
            this._isValidInput = this._isValidInput.filter(
              (item) => item !== maxCondition
            );
          }
        } else {
          this._isValidInput = this._isValidInput.filter(
            (item) => item !== minCondition && item !== maxCondition
          );
        }
      }

    private _validatePeriodInput(
        element: WeatherLimitElement, 
        value: number | null,
        max: number | undefined,
        type: "min" | "max",
    ) {

        if (value !== null && (value < 0 || value > (max ?? Infinity))) {
            this._isValidInput.push(`${element.name}+${type}`);
          } else {
            const index = this._isValidInput.indexOf(
              `${element.name}+${type}`
            );
            if (index > -1) {
              this._isValidInput.splice(index, 1);
            }
          }
    }

    private _onClose()
    {
        this._unit="m/s";
        this.shown=false;
        this.dispatchEvent(new Event('cancel'));
        
    }

    private async _onAccept()
    {
        if (this._isValidInput.length > 0) return;
    
        const mappedRouteLimits = mapWeatherLimits(this._weatherAvoidLimits, this._weatherWarnLimits);

        VoyageRoutingModel!.setWeatherLimits(mappedRouteLimits);
        let requestBody = {
            weatherLimit: this._weatherWarnLimits,
            type: 'WARN',
        }
        await this._postWeatherLimits(requestBody);
        this._unit="m/s";
        this.dispatchEvent(new Event('accept'));
    }
}
customElements.define('voyage-weather-limits', VoyageWeatherLimits);

